import type { ButtonHTMLAttributes, ReactNode } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md';
  children: ReactNode;
}

/**
 * Primary and ghost button variants.
 * Uses design tokens from tokens.css
 * Primary variant has Clerk-inspired glossy effect
 */
export function Button({
  variant = 'primary',
  size = 'md',
  children,
  className = '',
  disabled,
  ...props
}: ButtonProps) {
  const baseStyles =
    'inline-flex items-center justify-center rounded-md font-medium border focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2';

  const sizeStyles = {
    sm: {
      padding: 'var(--btn-sm-padding-y) var(--btn-sm-padding-x)',
      fontSize: 'var(--btn-sm-font-size)',
      fontWeight: 'var(--btn-sm-font-weight)',
    },
    md: {
      padding: 'var(--btn-md-padding-y) var(--btn-md-padding-x)',
      fontSize: 'var(--btn-md-font-size)',
      fontWeight: 'var(--btn-md-font-weight)',
    },
  };

  const variantStyles = {
    primary: [
      'bg-accent border-accent text-text-inverse',
      'transition-all duration-300 ease-[cubic-bezier(0.4,0.36,0,1)]',
      'hover:bg-accent-hover hover:border-accent-hover',
      'active:bg-accent-active active:border-accent-active',
      'disabled:opacity-50 disabled:cursor-not-allowed',
    ].join(' '),
    secondary: [
      // Base styles
      'relative isolate overflow-hidden',
      'bg-neutral border-neutral text-text-inverse',
      // Shadow
      'shadow-[0_1px_rgba(255,255,255,0.07)_inset,0_1px_3px_rgba(0,0,0,0.2)]',
      // Transition
      'transition-all duration-300 ease-[cubic-bezier(0.4,0.36,0,1)]',
      // Hover/active states
      'hover:bg-neutral-hover hover:border-neutral-hover',
      'active:bg-[#0f0f10] active:border-[#0f0f10]',
      'disabled:opacity-50 disabled:cursor-not-allowed',
    ].join(' '),
    ghost:
      'bg-transparent border-border-primary text-text-tertiary hover:bg-secondary active:bg-tertiary transition-all duration-300 ease-[cubic-bezier(0.4,0.36,0,1)] disabled:opacity-50 disabled:cursor-not-allowed',
  };

  return (
    <button
      className={`${baseStyles} ${variantStyles[variant]} ${className}`}
      style={sizeStyles[size]}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
}
